import { LitElement, html, nothing, type PropertyValues } from 'lit'
import { createRef, ref } from 'lit/directives/ref.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { customElement, property, state } from 'lit/decorators.js'
import { baseStyles } from '../../foundations/styles/index.js'
import { themeStyles } from '../../foundations/styles/themes.js'
import {
  HEADER_HEIGHT_PX,
  HEADER_STACK_HEIGHT_PX,
  HEADER_HEIGHT_TOKEN,
  HEADER_STACK_HEIGHT_TOKEN,
} from '../../foundations/geometry.js'
import { headerStyles } from './header.styles.js'
import { menuStyles } from './menu.styles.js'
import { type NavigationItem, type MenuClickDetail, type SearchSubmitDetail, type NavLoadDetail, type NavErrorDetail, type HeaderEventName, EVENTS } from '../../types/index.js'
import logoSvg from '../../assets/logo.svg'
import avatarSvg from '../../assets/avatar.svg'
import donateSvg from '../../assets/donate.svg'
import searchSvg from '../../assets/search.svg'
import searchActiveSvg from '../../assets/search-active.svg'
import {
  type SilentAuthResponse,
  createEmailSignupEvent,
  fetchHeaderNavigation,
  getApiConfig,
  normalizeAuthBaseUrl,
  sanitizeHeaderHref,
} from '@ligonier/shared'
import type { SearchPort, SearchSuggestion, SearchResult, SearchSelectDetail } from '@ligonier/shared/domain/search'
import { buildSearchUrl } from '@ligonier/shared/domain/search'
// AlgoliaSearchAdapter imported only here to instantiate the default — the composition root.
// All component logic talks to SearchPort only.
import { AlgoliaSearchAdapter } from '@ligonier/shared/adapters/algolia'
import type { AuthPort } from '@ligonier/shared/domain/auth'
// LigonierAuthAdapter imported only here to instantiate the default — the composition root.
// All component logic talks to AuthPort only.
import { LigonierAuthAdapter } from '@ligonier/shared/adapters/ligonier-auth'
// Shared with the footer so `="false"` (and attribute-removal) semantics stay
// identical across both components. Pair with useDefault: true — the converter
// returns null on attribute removal so the property reverts to its default.
import { booleanStringConverter } from '@ligonier/shared'

export type HeaderTheme = 'default' | 'cream' | 'clear'

@customElement('ligonier-header')
export class LigonierHeader extends LitElement {
  // Static styles
  static styles = [themeStyles, baseStyles, headerStyles, menuStyles]
  private static readonly supportsInert = 'inert' in HTMLElement.prototype

  private static readonly SEARCH_BLUR_CLOSE_DELAY_MS = 200
  private static instanceCounter = 0

  private readonly boundHandleEscapeKey = (e: KeyboardEvent) => this.handleEscapeKey(e)
  private readonly boundHandleOutsideClick = (e: Event) => this.handleOutsideClick(e)
  private readonly boundHandleScroll = () => {
    const scrolled = window.scrollY > 0
    this.toggleAttribute('data-scrolled', scrolled)
  }
  private readonly boundHandleResize = () => this.updateDesktopSearchFill()
  private readonly instanceId = ++LigonierHeader.instanceCounter
  private readonly desktopSearchFormId = `desktop-search-form-${this.instanceId}`
  private readonly desktopSearchInputId = `desktop-search-input-${this.instanceId}`
  private readonly menuContainerId = `header-menu-container-${this.instanceId}`

  /**
   * Expose the header's geometry to the host page so consuming apps can
   * position fixed elements (e.g. the alert banner) relative to the header
   * without hardcoding its dimensions. Published on the root element because
   * shadow :host custom properties aren't visible outside this component.
   * Values are design constants, so publishing is idempotent and intentionally
   * not cleaned up on disconnect (another instance — e.g. an error boundary's
   * header — may still rely on them; consumers carry fallbacks regardless).
   */
  private publishGeometryTokens() {
    const root = document.documentElement
    root.style.setProperty(HEADER_HEIGHT_TOKEN, `${HEADER_HEIGHT_PX}px`)
    root.style.setProperty(
      HEADER_STACK_HEIGHT_TOKEN,
      `${HEADER_STACK_HEIGHT_PX}px`,
    )
  }

  connectedCallback() {
    super.connectedCallback()
    this.publishGeometryTokens()
    // Use host-injected provider if set, otherwise build default Algolia adapter
    this._searchAdapter = this.searchProvider ?? new AlgoliaSearchAdapter({
      proxyUrl: this.searchProxyUrl,
      env: this.searchEnv,
    })
    // Use host-injected auth provider if set, otherwise build default Ligonier adapter
    if (this.authProvider) {
      this._authAdapter = this.authProvider
      this._ownsAuthAdapter = false
    } else {
      this._authAdapter = new LigonierAuthAdapter({
        authBaseUrl: this.authBaseUrl,
      })
      this._ownsAuthAdapter = true
    }
    this.requestSilentAuth()
    this.requestNavFetch()
    document.addEventListener('keydown', this.boundHandleEscapeKey)
    document.addEventListener('click', this.boundHandleOutsideClick)
    window.addEventListener('scroll', this.boundHandleScroll, { passive: true })
    window.addEventListener('resize', this.boundHandleResize, { passive: true })
    this.boundHandleScroll()
  }

  protected updated(changedProperties: PropertyValues<this>) {
    if (
      changedProperties.has('navItems' as keyof LigonierHeader) ||
      changedProperties.has('authStatus' as keyof LigonierHeader)
    ) {
      const previousAuthStatus = changedProperties.get(
        'authStatus' as keyof LigonierHeader,
      ) as SilentAuthResponse | undefined
      this.syncNavUiState(previousAuthStatus)
    }

    if (changedProperties.has('checkAuth')) {
      if (this.checkAuth) {
        this.requestSilentAuth()
      } else {
        this.abortSilentAuthRequest()
        this.resetAuthState()
      }
    }

    if (changedProperties.has('authBaseUrl') && !this.authProvider) {
      // Rebuild the default adapter so it uses the new base URL for future calls.
      if (this._ownsAuthAdapter) {
        this._authAdapter?.destroy?.()
      }
      this._authAdapter = new LigonierAuthAdapter({
        authBaseUrl: this.authBaseUrl,
      })
      this._ownsAuthAdapter = true
      this.requestSilentAuth()
    }

    if (changedProperties.has('showSearch') && !this.showSearch) {
      this.disableSearchUi()
    }

    // Reflect show-search so the header-spacer knows whether to reserve the
    // mobile search bar's height in addition to the header's.
    this.toggleAttribute('data-show-search', this.showSearch)

    if (changedProperties.has('menuOpen' as keyof LigonierHeader)) {
      this.toggleAttribute('data-menu-open', this.menuOpen)
    }

    if (changedProperties.has('searchOpen' as keyof LigonierHeader)) {
      // Reflected so the desktop layout can collapse the center nav while the
      // search field is expanded (avoids the nav + search bar getting cramped).
      this.toggleAttribute('data-search-open', this.searchOpen)
      this.updateDesktopSearchFill()
    }
  }

  // When the desktop search opens, decide between two behaviors based on the
  // actual space available (not a fixed viewport breakpoint):
  //   - "fill" mode: enough room, so keep the center nav and expand the search
  //     to fill the gap between the nav and the account icon (nav stays put;
  //     only the search bar resizes).
  //   - overlay mode: not enough room, so fade the nav out and use a fixed-width
  //     search overlay (handled purely in CSS via :not([data-search-fill])).
  private static readonly SEARCH_FILL_MIN_WIDTH = 240
  private static readonly SEARCH_FILL_MAX_WIDTH = 560
  private static readonly SEARCH_FILL_GAP = 24

  private updateDesktopSearchFill() {
    if (!this.searchOpen || !this.isConnected) {
      this.toggleAttribute('data-search-fill', false)
      this.style.removeProperty('--search-open-width')
      return
    }

    const nav = this.shadowRoot?.querySelector('.center-section')
    const container = this.shadowRoot?.querySelector('.search-container')
    if (!(nav instanceof HTMLElement) || !(container instanceof HTMLElement)) {
      return
    }

    // getBoundingClientRect stays valid even while the nav is visibility:hidden
    // (that state preserves layout), so measuring is safe in either mode.
    const available =
      container.getBoundingClientRect().right -
      nav.getBoundingClientRect().right -
      LigonierHeader.SEARCH_FILL_GAP

    if (available >= LigonierHeader.SEARCH_FILL_MIN_WIDTH) {
      const width = Math.min(available, LigonierHeader.SEARCH_FILL_MAX_WIDTH)
      this.style.setProperty('--search-open-width', `${width}px`)
      this.toggleAttribute('data-search-fill', true)
    } else {
      this.toggleAttribute('data-search-fill', false)
      this.style.removeProperty('--search-open-width')
    }
  }

  private syncNavUiState(
    previousAuthStatus?: SilentAuthResponse,
  ) {
    const validExpandableLabels = this.getValidExpandableLabels()

    const nextExpandedSections = new Set(
      Array.from(this.expandedSections).filter((label) => validExpandableLabels.has(label)),
    )

    // When the menu is open and nav loads asynchronously, initialize defaults if no sections are expanded
    if (this.menuOpen && nextExpandedSections.size === 0 && validExpandableLabels.size > 0) {
      const defaults = this.getDefaultExpandedSections()
      defaults.forEach((label) => nextExpandedSections.add(label))
    }

    const wasAuthenticated = previousAuthStatus?.authenticated ?? false
    if (!wasAuthenticated && this.isAuthenticated && this.menuOpen) {
      nextExpandedSections.add('My Account')
    }

    // Clear the dropdown if the user became unauthenticated — prevents it from
    // reopening automatically on a later authenticated transition.
    if (wasAuthenticated && !this.isAuthenticated) {
      this.accountDropdownOpen = false
    }

    this.updateExpandedSections(nextExpandedSections)
  }

  disconnectedCallback() {
    this._searchAdapter?.destroy?.()
    if (this._ownsAuthAdapter) {
      this._authAdapter?.destroy?.()
    }
    clearTimeout(this._debounceTimer)
    this.abortSilentAuthRequest()
    this.abortNavFetchRequest()
    this.cancelSearchCloseTimer()
    this.resetAuthState()
    this.resetTransientUiState()
    document.removeEventListener('keydown', this.boundHandleEscapeKey)
    document.removeEventListener('click', this.boundHandleOutsideClick)
    window.removeEventListener('scroll', this.boundHandleScroll)
    window.removeEventListener('resize', this.boundHandleResize)
    super.disconnectedCallback()
  }

  /**
   * Swap the search provider at any time after the element connects.
   * Prefer this over setting searchProvider before connect — it cleanly
   * destroys the previous adapter and wires up the new one without
   * triggering a redundant silent auth call.
   */
  setSearchProvider(provider: SearchPort) {
    this._searchAdapter?.destroy?.()
    this._searchAdapter = provider
  }

  /**
   * Swap the auth provider at any time after the element connects.
   * Triggers a fresh silent auth check with the new provider.
   * Prefer this over setting authProvider before connect — it cleanly
   * destroys the previous adapter and re-checks auth without triggering
   * a full reconnect cycle (no nav refetch, no duplicate listener registration).
   */
  setAuthProvider(provider: AuthPort) {
    if (this._ownsAuthAdapter) {
      this._authAdapter?.destroy?.()
    }
    this._authAdapter = provider
    this._ownsAuthAdapter = false  // host owns the lifecycle of the injected provider
    // Keep the public property in sync so updated() can distinguish an injected
    // provider from the default adapter when auth-base-url changes later.
    this.authProvider = provider
    this.requestSilentAuth()
  }

  // ─── Public properties ────────────────────────────────────────────────────

  @property({ type: String, attribute: 'logo-src' })
  logoSrc = logoSvg

  @property({ type: String, attribute: 'avatar-src' })
  avatarSrc = avatarSvg

  @property({ type: String, attribute: 'donate-icon-src' })
  donateIconSrc = donateSvg

  @property({ converter: booleanStringConverter, attribute: 'show-partner-button', useDefault: true })
  showPartnerButton = true

  @property({ converter: booleanStringConverter, attribute: 'show-donate-button', useDefault: true })
  showDonateButton = true

  @property({ converter: booleanStringConverter, attribute: 'show-search', useDefault: true })
  showSearch = true

  @property({ converter: booleanStringConverter, attribute: 'check-auth', useDefault: true })
  checkAuth = true

  private _authBaseUrl = ''

  @property({ type: String, attribute: 'auth-base-url' })
  get authBaseUrl() {
    return this._authBaseUrl || getApiConfig().authBaseUrl || 'https://auth.ligonier.org'
  }

  set authBaseUrl(value: string) {
    const normalizedAuthBaseUrl = normalizeAuthBaseUrl(value, '')
    const previousAuthBaseUrl = this._authBaseUrl
    if (normalizedAuthBaseUrl === previousAuthBaseUrl) {
      return
    }

    this._authBaseUrl = normalizedAuthBaseUrl
    this.requestUpdate('authBaseUrl', previousAuthBaseUrl)
  }

  private _accountBaseUrl = ''

  @property({ type: String, attribute: 'account-base-url' })
  get accountBaseUrl() {
    return this._accountBaseUrl || getApiConfig().accountBaseUrl || 'https://account.ligonier.org'
  }

  set accountBaseUrl(value: string) {
    const normalized = normalizeAuthBaseUrl(value, '')
    const previous = this._accountBaseUrl
    if (normalized === previous) {
      return
    }

    this._accountBaseUrl = normalized
    this.requestUpdate('accountBaseUrl', previous)
  }

  @property({ type: String, attribute: 'search-proxy-url' })
  searchProxyUrl = 'https://search.ligonier.org/api.search'

  @property({ type: String, attribute: 'search-url' })
  searchUrl = '/search'

  @property({ type: String, attribute: 'search-env' })
  searchEnv = 'prod'

  @property({
    reflect: true,
    converter: {
      fromAttribute: (value: string | null): HeaderTheme => {
        if (value === 'cream' || value === 'clear') return value
        return 'default'
      },
      toAttribute: (value: HeaderTheme): string | null => {
        return value === 'default' ? null : value
      },
    },
  })
  theme: HeaderTheme = 'default'

  /**
   * Programmatic dependency injection — not an HTML attribute.
   * Set this to swap in any SearchPort implementation:
   *   header.searchProvider = new MeilisearchAdapter(...)
   * Must be set before the element connects to the DOM.
   */
  searchProvider?: SearchPort

  /**
   * Programmatic dependency injection — not an HTML attribute.
   * Set this to swap in any AuthPort implementation:
   *   header.authProvider = new MyAuthAdapter(...)
   * Must be set before the element connects to the DOM.
   */
  authProvider?: AuthPort

  // ─── Internal state ───────────────────────────────────────────────────────

  @state()
  private menuOpen = false


  @state()
  private searchOpen = false

  @state()
  private searchQuery = ''

  @state()
  private searchSuggestions: SearchSuggestion[] = []

  @state()
  private searchResults: SearchResult[] = []

  @state()
  private searchLoading = false

  @state()
  private searchError: string | null = null

  @state()
  private activeIndex = -1

  @state()
  private authStatus: SilentAuthResponse = { authenticated: false }

  @state()
  private authLoading = false

  @state()
  private accountDropdownOpen = false

  @state()
  private navLoading = false

  @state()
  private navLoadError: unknown = null

  @state()
  private navItems: NavigationItem[] = []

  @state()
  private expandedSections: Set<string> = new Set()

  private authAbortController?: AbortController
  private navAbortController?: AbortController
  private searchCloseTimer?: ReturnType<typeof setTimeout>
  private nextRefreshTime: number | null = null
  private pendingNavItems: NavigationItem[] | null = null
  private pendingAuthStatus: SilentAuthResponse | null = null
  private pendingLoginRedirect = false
  private loginRedirectTimer?: ReturnType<typeof setTimeout>
  private readonly desktopSearchInputRef = createRef<HTMLInputElement>()
  private _searchAdapter!: SearchPort
  private _authAdapter!: AuthPort
  private _ownsAuthAdapter = false
  private _debounceTimer?: ReturnType<typeof setTimeout>

  private static isAbortError(error: unknown): boolean {
    if (error instanceof DOMException) {
      return error.name === 'AbortError'
    }

    return error instanceof Error && error.name === 'AbortError'
  }

  private static areSetsEqual<T>(left: Set<T>, right: Set<T>): boolean {
    if (left.size !== right.size) {
      return false
    }

    for (const value of left) {
      if (!right.has(value)) {
        return false
      }
    }

    return true
  }

  private hasChildren(
    item: NavigationItem,
  ): item is NavigationItem & { children: NonNullable<NavigationItem['children']> } {
    return Array.isArray(item.children) && item.children.length > 0
  }

  private getDefaultExpandedSections(): Set<string> {
    const firstSection = this.navItems.find((item) => this.hasChildren(item))
    const defaults = firstSection ? [firstSection.label] : []

    // The "My Account" section is always rendered (signed in *and* out), so it
    // is always default-expanded — otherwise the signed-out section renders
    // collapsed/empty and the toggle is a no-op (DEV-1155).
    defaults.unshift('My Account')

    return new Set(defaults)
  }

  private getValidExpandableLabels(): Set<string> {
    const validExpandableLabels = new Set(
      this.navItems
        .filter((item) => this.hasChildren(item))
        .map((item) => item.label),
    )

    // Always expandable — the section exists in both auth states (DEV-1155).
    validExpandableLabels.add('My Account')

    return validExpandableLabels
  }

  private get desktopNavItems() {
    return this.navItems.filter((item) => this.hasChildren(item))
  }

  // ─── Computed ─────────────────────────────────────────────────────────────

  private get isAuthenticated() {
    return this.authStatus?.authenticated ?? false
  }

  /**
   * Account portal origin — used for My Account and My Library links.
   * Sourced from the `account-base-url` attribute or `VITE_ACCOUNT_BASE_URL`
   * env var, falling back to `https://account.ligonier.org`.
   */
  private get accountOrigin(): string {
    return this.accountBaseUrl
  }

  private get accountUrl(): string {
    return `${this.accountOrigin}/dashboard/`
  }

  private get libraryUrl(): string {
    return `${this.accountOrigin}/dashboard/learning-library/`
  }

  /** Logout URL on the auth server — navigating here destroys the session cookie. */
  private get logoutUrl(): string {
    try {
      return `${new URL(this.authBaseUrl).origin}/logout`
    } catch {
      return 'https://auth.ligonier.org/logout'
    }
  }

  private loginUrl(redirectUri: string, grantType: string = 'implicit_cookie'): string {
    return this.authActionUrl('/authorize', redirectUri, grantType)
  }

  private registerUrl(redirectUri: string, grantType: string = 'implicit_cookie'): string {
    return this.authActionUrl('/register', redirectUri, grantType)
  }

  private authActionUrl(pathname: string, redirectUri: string, grantType: string): string {
    let url: URL

    try {
      url = new URL(this.authBaseUrl)
      url.pathname = pathname
    } catch {
      url = new URL(`https://auth.ligonier.org${pathname}`)
    }

    url.searchParams.set('redirectUri', redirectUri)
    url.searchParams.set('grantType', grantType)
    return url.toString()
  }

  private get searchIconSrc() {
    return this.searchOpen ? searchActiveSvg : searchSvg
  }

  private get searchButtonAriaLabel() {
    return this.searchOpen ? 'Close search' : 'Open search'
  }

  private get trimmedSearchQuery() {
    return this.searchQuery.trim()
  }

  private get hasSearchQuery() {
    return this.trimmedSearchQuery.length > 0
  }

  private get menuButtonLabel() {
    return this.menuOpen ? 'CLOSE' : 'MENU'
  }

  private get canHandleSearchInteractions() {
    return this.showSearch && this.isConnected
  }

  private get canRequestNav() {
    return this.isConnected
  }

  private get hasNavItems() {
    return this.navItems.length > 0
  }

  private get shouldShowMenuLoadingState() {
    return this.menuOpen && this.navLoading && !this.hasNavItems
  }

  private get shouldShowMenuErrorState() {
    return this.menuOpen && !this.navLoading && this.navLoadError !== null && !this.hasNavItems
  }

  private get shouldShowMenuEmptyState() {
    return this.menuOpen && !this.navLoading && this.navLoadError === null && !this.hasNavItems
  }

  private get dropdownItemCount(): number {
    return this.searchSuggestions.length + this.searchResults.length
  }

  private get showDropdown(): boolean {
    return (
      this.searchOpen &&
      this.searchQuery.trim().length >= 2 &&
      (this.dropdownItemCount > 0 || this.searchLoading || this.searchError !== null)
    )
  }

  // TODO: Update this to use proper alt-text variants
  private get avatarAltText() {
    const { firstName, lastName, email } = this.authStatus || {}
    const name = [firstName, lastName].filter(Boolean).join(' ').trim()
    if (name) return `${name} profile`
    if (email) return `${email} profile`
    return 'User Avatar'
  }

  private emitEvent<TDetail = undefined>(name: HeaderEventName, detail?: TDetail) {
    this.dispatchEvent(new CustomEvent<TDetail>(name, {
      bubbles: true,
      composed: true,
      ...(detail === undefined ? {} : { detail }),
    }))
  }

  // ─── Event handlers ───────────────────────────────────────────────────────

  private handleMenuClick() {
    if (!this.isConnected) {
      return
    }

    const nextState = !this.menuOpen
    this.menuOpen = nextState

    if (nextState) {
      this.accountDropdownOpen = false
      this.applyPendingAuthStatus()
      this.applyPendingNavItems()
      this.updateExpandedSections(this.getDefaultExpandedSections())

      // Re-check auth silently whenever the mobile menu opens to detect cross-site
      // auth changes in either direction — unconditional because the mobile menu
      // is visible to both authenticated and unauthenticated users.
      this.requestSilentAuth()

      // Refetch if refresh window has passed, or if previous load failed
      const isRefreshDue = this.nextRefreshTime !== null && Date.now() > this.nextRefreshTime
      if (isRefreshDue || (this.navLoadError !== null && !this.navLoading && !this.hasNavItems)) {
        this.requestNavFetch()
      }
    } else {
      this.applyPendingAuthStatus()
      this.applyPendingNavItems()
    }

    this.emitEvent<MenuClickDetail>(EVENTS.MENU_CLICK, { open: nextState })
  }

  private handleMenuClose() {
    if (!this.isConnected) {
      return
    }

    if (!this.menuOpen) {
      return
    }

    this.menuOpen = false
    this.applyPendingAuthStatus()
    this.applyPendingNavItems()
    this.emitEvent(EVENTS.MENU_CLOSE)
  }

  private handleEmailSignup = (e: Event) => {
    e.preventDefault()
    if (!this.isConnected) {
      return
    }

    const form = e.target as HTMLFormElement
    const email = (new FormData(form).get('email') as string | null)?.trim() ?? ''
    if (!email) {
      return
    }

    // Reuse the shared email-signup event so header and footer behave identically;
    // the host site listens for `email-signup` and submits to its backend.
    this.dispatchEvent(createEmailSignupEvent(email))
    form.reset()
  }

  private async handleSilentAuth() {
    this.abortSilentAuthRequest()
    const abortController = new AbortController()
    this.authAbortController = abortController
    this.authLoading = true

    try {
      const result = await this._authAdapter.checkSilentAuth({
        abortSignal: abortController.signal
      })

      if (!this.isConnected || this.authAbortController !== abortController) {
        return
      }

      this.stageOrApplyAuthStatus(result)
    } catch (error) {
      if (this.authAbortController !== abortController) {
        return
      }

      if (LigonierHeader.isAbortError(error)) {
        return
      }

      this.stageOrApplyAuthStatus({ authenticated: false, error: 'silent-auth-failed' })
    } finally {
      if (this.authAbortController === abortController) {
        this.authAbortController = undefined
        this.authLoading = false
      }
    }
  }

  private async handleNavFetch() {
    this.abortNavFetchRequest()
    const abortController = new AbortController()
    this.navAbortController = abortController
    this.navLoading = true
    this.navLoadError = null

    try {
      const items = await fetchHeaderNavigation(abortController.signal)

      if (!this.isConnected || this.navAbortController !== abortController) {
        return
      }

      // Component is API-authoritative: always apply fetched items.
      // Consumer-provided navItems are not supported; all navigation data flows from the API.
      const applied = this.stageOrApplyNavItems(items)
      this.navLoadError = null
      // Schedule refresh for 15 seconds into the next clock hour
      const nextHour = new Date()
      nextHour.setHours(nextHour.getHours() + 1, 0, 15, 0)
      this.nextRefreshTime = nextHour.getTime()

      this.emitEvent<NavLoadDetail>(EVENTS.NAV_LOAD, { items, applied })
    } catch (error) {
      if (this.navAbortController !== abortController) {
        return
      }

      if (LigonierHeader.isAbortError(error)) {
        return
      }

      this.navLoadError = error
      console.error('Failed to fetch header navigation:', error)
      this.emitEvent<NavErrorDetail>(EVENTS.NAV_ERROR, { error })
    } finally {
      if (this.navAbortController === abortController) {
        this.navAbortController = undefined
        this.navLoading = false
      }
    }
  }

  private requestNavFetch() {
    if (!this.canRequestNav) {
      return
    }

    void this.handleNavFetch()
  }

  private requestSilentAuth() {
    if (!this.checkAuth || !this.isConnected) {
      if (!this.checkAuth) {
        this.resetAuthState()
      }
      return
    }

    void this.handleSilentAuth()
  }

  // Nav loading and apply behavior:
  // - Fetches are triggered on connect and on menu open after nextRefreshTime.
  // - nextRefreshTime is set shortly after the next hour to avoid top-of-hour timing edge cases.
  // - This same next-hour scheduling also naturally handles midnight rollover.
  // - If no menu data is currently rendered (loading/error/empty), apply fetched data immediately.
  // - If menu data is visible while the menu is open, defer applying fetched data to avoid shifting click targets.
  // - Deferred data is applied when the menu closes (or before the next open render).
  private stageOrApplyNavItems(items: NavigationItem[]) {
    if (this.menuOpen && this.hasNavItems) {
      this.pendingNavItems = items
      return false
    }

    this.pendingNavItems = null
    this.navItems = items
    return true
  }

  private applyPendingNavItems() {

    if (!this.pendingNavItems) {
      return false
    }

    this.navItems = this.pendingNavItems
    this.pendingNavItems = null
    return true
  }

  // Deferred auth status.
  // We only defer when the DESKTOP account dropdown is open, because applying a
  // new status there can yank the dropdown closed mid-interaction (it renders
  // nothing when unauthenticated). The MOBILE menu is different: its account
  // section is always rendered and must reflect the real login state, so a
  // status arriving while the menu is open is applied immediately. Deferring it
  // (the previous behavior) left authenticated users seeing "Sign in" for the
  // whole time the menu was open — and also defeated the menu-open auto-expand
  // of "My Account", which depends on isAuthenticated flipping while menuOpen.
  private stageOrApplyAuthStatus(status: SilentAuthResponse): void {
    if (this.pendingLoginRedirect) {
      clearTimeout(this.loginRedirectTimer)
      this.loginRedirectTimer = undefined
      this.pendingLoginRedirect = false
      this.pendingAuthStatus = null
      this.authStatus = status
      this.emitEvent(EVENTS.AUTH_STATUS, status)
      if (status.authenticated) {
        // They signed in on another tab — open the dropdown rather than redirecting.
        // Only open the desktop dropdown if the mobile menu is not open.
        if (!this.menuOpen) {
          this.accountDropdownOpen = true
        }
        this.requestSilentAuth()
      } else {
        // Still not authenticated — proceed with the login redirect.
        const event = new CustomEvent(EVENTS.AUTH_LOGIN_CLICK, {
          bubbles: true,
          composed: true,
          cancelable: true,
        })
        const prevented = !this.dispatchEvent(event)
        if (!prevented) {
          window.location.href = this.loginUrl(window.location.href)
        }
      }
      return
    }

    if (this.accountDropdownOpen) {
      this.pendingAuthStatus = status
      return
    }
    this.pendingAuthStatus = null
    this.authStatus = status
    this.emitEvent(EVENTS.AUTH_STATUS, status)
  }

  private applyPendingAuthStatus(): void {
    if (this.pendingAuthStatus === null) return
    const status = this.pendingAuthStatus
    this.pendingAuthStatus = null
    this.authStatus = status
    this.emitEvent(EVENTS.AUTH_STATUS, status)
  }

  private updateExpandedSections(nextExpandedSections: Set<string>) {
    if (LigonierHeader.areSetsEqual(nextExpandedSections, this.expandedSections)) {
      return
    }

    this.expandedSections = nextExpandedSections
  }

  private disableSearchUi() {
    this.closeSearch(true)
  }

  private resetAuthState() {
    this.pendingAuthStatus = null
    this.authStatus = { authenticated: false }
    this.authLoading = false
  }

  private abortSilentAuthRequest() {
    this.authAbortController?.abort()
    this.authAbortController = undefined
  }

  private abortNavFetchRequest() {
    this.navAbortController?.abort()
    this.navAbortController = undefined
  }

  private resetTransientUiState() {
    this.menuOpen = false
    this.accountDropdownOpen = false
    this.pendingAuthStatus = null
    clearTimeout(this.loginRedirectTimer)
    this.loginRedirectTimer = undefined
    this.pendingLoginRedirect = false
    this.disableSearchUi()
    this.expandedSections = new Set()
    this.pendingNavItems = null
  }

  private handleMenuOverlayClick(e: Event) {
    if (!this.isConnected) {
      return
    }

    if (e.target === e.currentTarget) {
      this.handleMenuClose()
    }
  }

  private emitAndNavigate(eventName: HeaderEventName, href: string) {
    this.emitEvent(eventName)
    window.location.href = href
  }

  private get logoHref(): string {
    const raw = getApiConfig().ligonierOrgUrl || 'https://www.ligonier.org'
    try {
      const parsed = new URL(raw.trim())
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') throw new Error('Invalid scheme')
      return parsed.origin
    } catch {
      return 'https://www.ligonier.org'
    }
  }

  private handleLogoClick() {
    if (!this.isConnected) {
      return
    }

    this.emitEvent(EVENTS.LOGO_CLICK)
  }

  private handlePartnerClick() {
    if (!this.isConnected) {
      return
    }

    const rawBase = getApiConfig().ligonierOrgUrl || 'https://www.ligonier.org'
    let base: string
    try {
      const parsed = new URL(rawBase.trim())
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') throw new Error('Invalid scheme')
      base = parsed.origin
    } catch {
      base = 'https://www.ligonier.org'
    }
    this.emitAndNavigate(EVENTS.PARTNER_CLICK, `${base}/give/partner`)
  }

  private handleDonateClick() {
    if (!this.isConnected) {
      return
    }

    const rawUrl = getApiConfig().donateUrl || 'https://donate.ligonier.org'
    let url: string
    try {
      const parsed = new URL(rawUrl.trim())
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') throw new Error('Invalid scheme')
      url = parsed.href
    } catch {
      url = 'https://donate.ligonier.org'
    }
    const event = new CustomEvent(EVENTS.DONATE_CLICK, {
      bubbles: true,
      composed: true,
      cancelable: true,
    })
    const prevented = !this.dispatchEvent(event)
    if (!prevented) {
      window.location.href = url
    }
  }

  // ─── Auth handlers ────────────────────────────────────────────────────────

  private handleAvatarClick() {
    if (!this.isConnected) {
      return
    }

    if (!this.isAuthenticated) {
      // Fire a silent auth check before redirecting — the user may have signed in
      // on another tab and the component's cached state is stale. If the check comes
      // back authenticated we open the dropdown instead of redirecting to login.
      // A 500ms deadline aborts the check and falls through to the redirect so
      // slow-connection users are never left waiting indefinitely.
      this.pendingLoginRedirect = true
      this.loginRedirectTimer = setTimeout(() => {
        if (!this.pendingLoginRedirect) return
        this.pendingLoginRedirect = false
        this.authLoading = false
        this.abortSilentAuthRequest()
        const event = new CustomEvent(EVENTS.AUTH_LOGIN_CLICK, {
          bubbles: true,
          composed: true,
          cancelable: true,
        })
        const prevented = !this.dispatchEvent(event)
        if (!prevented) {
          window.location.href = this.loginUrl(window.location.href)
        }
      }, 500)
      this.requestSilentAuth()
      return
    }

    const nextOpen = !this.accountDropdownOpen
    this.accountDropdownOpen = nextOpen

    if (nextOpen) {
      // Apply any auth result that arrived while the dropdown was last closed,
      // then fire a fresh check. The dropdown shows current state immediately;
      // any new result is staged until the next open.
      this.applyPendingAuthStatus()
      this.requestSilentAuth()
    } else {
      this.applyPendingAuthStatus()
    }
  }

  private handleSignOut() {
    if (!this.isConnected) {
      return
    }

    // Abort any in-flight silent auth check so its result cannot overwrite the
    // authenticated:false state we're about to set. Also discard any already-staged
    // result — a pending auth response must not resurrect the session after sign-out.
    this.abortSilentAuthRequest()
    this.pendingAuthStatus = null
    clearTimeout(this.loginRedirectTimer)
    this.loginRedirectTimer = undefined
    this.pendingLoginRedirect = false

    // Clear local auth state immediately — don't wait for the server.
    // Sign-out is always honoured locally; the network call is best-effort.
    // authLoading may still be true if the aborted handleSilentAuth couldn't
    // clear it (its finally guard checks authAbortController identity, which
    // abortSilentAuthRequest() cleared above).
    this.authLoading = false
    this.authStatus = { authenticated: false }
    this.accountDropdownOpen = false

    // Start the adapter's logout — clearToken() runs synchronously inside logout()
    // before its first await, so localStorage tokens are cleared before the
    // auth-logout event fires below.
    this._authAdapter.logout().catch((error: unknown) => {
      console.error('[ligonier-header] Sign out request failed:', error)
    })

    // Emit after logout() has been called so local token cleanup has already run.
    this.emitEvent(EVENTS.AUTH_LOGOUT)

    // Navigate to the auth server's logout page — the only way to destroy the
    // server-side session cookie. The auth server clears the cookie and redirects
    // back to redirectUri, returning the user to wherever they signed out from.
    window.location.href = `${this.logoutUrl}?redirectUri=${encodeURIComponent(window.location.href)}`
  }

  // ─── Search handlers ──────────────────────────────────────────────────────

  private async handleSearchClick() {
    if (!this.canHandleSearchInteractions) {
      return
    }

    this.cancelSearchCloseTimer()
    const nextSearchOpen = !this.searchOpen
    if (nextSearchOpen) {
      this.searchOpen = true
      await this.updateComplete
      if (!this.canHandleSearchInteractions) {
        return
      }
      this.desktopSearchInputRef.value?.focus()
    } else {
      this.closeSearch(true)
    }
  }

  private handleSearchInput(e: Event) {
    if (!this.canHandleSearchInteractions) {
      return
    }

    const input = e.currentTarget
    if (!(input instanceof HTMLInputElement)) {
      return
    }

    const query = input.value
    if (this.searchQuery !== query) {
      this.searchQuery = query
    }

    this.activeIndex = -1
    clearTimeout(this._debounceTimer)

    if (query.trim().length < 2) {
      this.searchSuggestions = []
      this.searchResults = []
      this.searchLoading = false
      return
    }

    this.searchLoading = true
    this._debounceTimer = setTimeout(async () => {
      try {
        const result = await this._searchAdapter.search(query.trim())
        this.searchSuggestions = result.suggestions
        this.searchResults = result.results
        this.searchError = null
        this.activeIndex = -1
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return
        this.searchError = 'Unable to load suggestions'
        this.searchSuggestions = []
        this.searchResults = []
      } finally {
        this.searchLoading = false
      }
    }, 500)
  }

  private handleSearchSubmit(e: Event) {
    e.preventDefault()
    if (!this.canHandleSearchInteractions) {
      return
    }

    if (this.hasSearchQuery) {
      this.emitEvent<SearchSubmitDetail>(EVENTS.SEARCH_SUBMIT, { query: this.trimmedSearchQuery })
    }

    this.searchSuggestions = []
    this.searchResults = []
    this.activeIndex = -1
  }

  private handleSearchBlur() {
    if (!this.canHandleSearchInteractions) {
      return
    }

    // Close search if input is empty
    if (!this.hasSearchQuery) {
      this.closeSearchWithDelay()
    }
  }

  private handleSearchFocus() {
    if (!this.canHandleSearchInteractions) {
      return
    }

    this.cancelSearchCloseTimer()
    if (!this.searchOpen) {
      this.searchOpen = true
    }
  }

  private closeSearchWithDelay() {
    if (!this.searchOpen) {
      return
    }

    this.cancelSearchCloseTimer()
    this.searchCloseTimer = setTimeout(() => {
      this.searchCloseTimer = undefined
      if (!this.isConnected) {
        return
      }
      this.closeSearch()
    }, LigonierHeader.SEARCH_BLUR_CLOSE_DELAY_MS)
  }

  private closeSearch(clearQuery = false) {
    this.cancelSearchCloseTimer()
    if (this.searchOpen) {
      this.searchOpen = false
    }
    if (clearQuery && this.searchQuery.length > 0) {
      this.searchQuery = ''
    }
  }

  private cancelSearchCloseTimer() {
    clearTimeout(this.searchCloseTimer)
    this.searchCloseTimer = undefined
  }

  private handleSearchKeydown(e: KeyboardEvent) {
    const total = this.dropdownItemCount

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        this.activeIndex = total === 0 ? -1 : (this.activeIndex + 1) % total
        break

      case 'ArrowUp':
        e.preventDefault()
        this.activeIndex =
          total === 0 ? -1
            : this.activeIndex <= 0 ? total - 1
              : this.activeIndex - 1
        break

      case 'Enter':
        if (this.activeIndex >= 0) {
          e.preventDefault()
          this._activateDropdownItem(this.activeIndex)
        }
        // activeIndex === -1: fall through to form submit via handleSearchSubmit
        break

      case 'Escape':
        clearTimeout(this._debounceTimer)
        this._clearSearch()
        this.closeSearch(true)
        break
    }
  }

  private _activateDropdownItem(index: number) {
    if (index < this.searchSuggestions.length) {
      this._handleSuggestionSelect(this.searchSuggestions[index]!)
    } else {
      this._handleResultSelect(this.searchResults[index - this.searchSuggestions.length]!)
    }
  }

  private _handleSuggestionSelect(suggestion: SearchSuggestion) {
    const event = new CustomEvent<SearchSelectDetail>('search-select', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: { type: 'suggestion', query: suggestion.query },
    })
    const prevented = !this.dispatchEvent(event)
    if (!prevented) {
      window.location.href = buildSearchUrl(this.searchUrl, suggestion.query)
    }
  }

  private _handleResultSelect(result: SearchResult) {
    const event = new CustomEvent<SearchSelectDetail>('search-select', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: { type: 'result', result },
    })
    const prevented = !this.dispatchEvent(event)
    if (!prevented) {
      window.location.href = result.url || buildSearchUrl(this.searchUrl, this.searchQuery)
    }
  }

  private _clearSearch() {
    clearTimeout(this._debounceTimer)
    this.searchQuery = ''
    this.searchSuggestions = []
    this.searchResults = []
    this.searchError = null
    this.activeIndex = -1
    this.searchLoading = false
  }

  private getMobileAccountChildren() {
    return [
      { label: 'My Account', href: this.accountUrl },
      { label: 'My Library', href: this.libraryUrl },
    ]
  }

  private static getAccordionIds(label: string, suffix: string | number) {
    return {
      buttonId: LigonierHeader.toId(label, 'accordion-button', suffix),
      contentId: LigonierHeader.toId(label, 'accordion-content', suffix),
    }
  }

  private isSectionExpanded(label: string): boolean {
    return this.expandedSections.has(label)
  }

  private static getEventLinkClass(baseClassName: string, hasMeta?: string): string {
    return `${baseClassName}${hasMeta ? ' event-link' : ''}`
  }

  private static renderDivider(className: string) {
    return html`<div class="${className}" aria-hidden="true"></div>`
  }

  private static renderActionButton(
    label: string,
    className: string,
    onClick: (event: Event) => void,
  ) {
    return html`
      <button type="button" class="${className}" @click=${onClick}>${label}</button>
    `
  }

  private renderMenuStatusState() {
    if (this.shouldShowMenuLoadingState) {
      return html`
        <div class="menu-empty-state loading" role="status" aria-live="polite">
          <div class="menu-empty-state-title">Loading menu...</div>
          <div class="menu-empty-state-subtitle">Please wait while items are loaded.</div>
        </div>
      `
    }

    if (this.shouldShowMenuErrorState) {
      return html`
        <div class="menu-empty-state error" role="alert" aria-live="assertive">
          <div class="menu-empty-state-title">Unable to load menu</div>
          <div class="menu-empty-state-subtitle">Please try again in a moment.</div>
        </div>
      `
    }

    if (this.shouldShowMenuEmptyState) {
      return html`
        <div class="menu-empty-state" role="status" aria-live="polite">
          <div class="menu-empty-state-title">Menu is currently unavailable</div>
          <div class="menu-empty-state-subtitle">Please check back soon.</div>
        </div>
      `
    }

    return nothing
  }

  private static renderPromotionBlock(
    label: string | undefined,
    href: string | undefined,
    dividerClassName: string,
    linkClassName: string,
    title?: string,
    subtitle?: string,
    wrapperClassName?: string,
    titleClassName?: string,
    subtitleClassName?: string,
  ) {
    const safeHref = LigonierHeader.sanitizeHref(href)
    if (!label || !safeHref) return nothing
    const baseClassName = linkClassName.split(' ')[0] || linkClassName
    const resolvedTitleClassName = titleClassName || `${baseClassName} promo-title`
    const resolvedSubtitleClassName = subtitleClassName || `${baseClassName} promo-subtitle`

    const inner = html`
      ${title ? html`<div class="${resolvedTitleClassName}">${title}</div>` : ''}
      ${LigonierHeader.renderSafeLinkOrText(label, safeHref, linkClassName)}
      ${subtitle ? html`<div class="${resolvedSubtitleClassName}">${subtitle}</div>` : ''}
    `

    return html`
      ${LigonierHeader.renderDivider(dividerClassName)}
      ${wrapperClassName ? html`<div class="${wrapperClassName}">${inner}</div>` : inner}
    `
  }

  private handleEscapeKey(e: KeyboardEvent) {
    if (!this.isConnected) {
      return
    }

    if (e.key !== 'Escape') return
    if (this.accountDropdownOpen) {
      this.accountDropdownOpen = false
      this.applyPendingAuthStatus()
    } else if (this.menuOpen) {
      this.handleMenuClose()
    } else if (this.searchOpen) {
      this.closeSearch(true)
    }
  }

  private handleOutsideClick(e: Event) {
    if (!this.isConnected) {
      return
    }

    const eventPath = e.composedPath()
    const clickedInsideHeader = eventPath.includes(this)

    if (this.accountDropdownOpen && !clickedInsideHeader) {
      this.accountDropdownOpen = false
      this.applyPendingAuthStatus()
    }

    if (this.menuOpen && !clickedInsideHeader) {
      this.handleMenuClose()
    }
  }

  private handleSectionToggle(label: string) {
    if (!this.isConnected) {
      return
    }

    if (!this.menuOpen) {
      return
    }

    if (!this.getValidExpandableLabels().has(label)) {
      return
    }

    const nextExpandedSections = new Set(this.expandedSections)
    if (nextExpandedSections.has(label)) {
      nextExpandedSections.delete(label)
    } else {
      nextExpandedSections.add(label)
    }
    this.updateExpandedSections(nextExpandedSections)
  }

  private static toId(value: string, prefix: string, suffix?: string | number) {
    const slug = value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
    const baseId = `${prefix}-${slug || 'section'}`
    return suffix !== undefined ? `${baseId}-${suffix}` : baseId
  }

  private static sanitizeHref(href?: string): string | undefined {
    const sanitized = sanitizeHeaderHref(href)
    return sanitized === '#' ? undefined : sanitized
  }

  private static renderSafeLinkOrText(
    label: string,
    href: string | undefined,
    className: string,
    fallbackTag: 'span' | 'div' = 'span',
  ) {
    const safeHref = LigonierHeader.sanitizeHref(href)
    if (safeHref) {
      return html`<a href="${safeHref}" class="${className}">${label}</a>`
    }

    if (fallbackTag === 'div') {
      return html`<div class="${className}">${label}</div>`
    }

    return html`<span class="${className}">${label}</span>`
  }

  private renderAccordionSection(
    label: string,
    buttonId: string,
    contentId: string,
    isExpanded: boolean,
    content: unknown,
  ) {
    const shouldHideWhenCollapsed = !isExpanded && !LigonierHeader.supportsInert

    return html`
      <div class="accordion-section">
        <button
          type="button"
          id="${buttonId}"
          class="accordion-toggle ${isExpanded ? 'expanded' : ''}"
          @click=${() => this.handleSectionToggle(label)}
          aria-expanded="${String(isExpanded)}"
          aria-controls="${contentId}"
        >
          <span class="toggle-label">${label}</span>
          ${LigonierHeader.renderAccordionToggleIcon()}
        </button>
        <div
          id="${contentId}"
          class="accordion-content ${isExpanded ? 'expanded' : ''}"
          ?hidden=${shouldHideWhenCollapsed}
          ?inert=${!isExpanded}
          aria-hidden="${String(!isExpanded)}"
          role="region"
          aria-labelledby="${buttonId}"
        >
          ${content}
        </div>
      </div>
    `
  }

  private static renderAccordionToggleIcon() {
    return html`
      <span class="toggle-icon" aria-hidden="true">
        <svg viewBox="0 0 12 12" class="toggle-icon-svg" focusable="false">
          <path d="M2 4.25L6 8.25L10 4.25" />
        </svg>
      </span>
    `
  }

  // ─── Render helpers ───────────────────────────────────────────────────────

  private renderSearchDropdown() {
    if (!this.showDropdown) return html``

    let itemIndex = 0

    return html`
      <div class="search-dropdown" role="listbox" aria-label="Search suggestions">

        ${this.searchLoading ? html`
          <div class="search-dropdown-loading" role="status" aria-live="polite">
            <span class="search-spinner"></span>
            <span class="visually-hidden">Loading suggestions…</span>
          </div>
        ` : ''}

        ${this.searchError ? html`
          <div class="search-dropdown-error" role="alert">${this.searchError}</div>
        ` : ''}

        ${this.searchSuggestions.length > 0 ? html`
          <div class="search-dropdown-section">
            <div class="search-dropdown-section-label">Suggestions</div>
            ${this.searchSuggestions.map((suggestion) => {
      const idx = itemIndex++
      return html`
                <div
                  class="search-dropdown-item search-dropdown-item--suggestion ${idx === this.activeIndex ? 'search-dropdown-item--active' : ''}"
                  role="option"
                  aria-selected="${idx === this.activeIndex}"
                  @mousedown=${(e: Event) => { e.preventDefault(); this._handleSuggestionSelect(suggestion) }}
                >
                  <span class="search-dropdown-suggestion-query">${suggestion.query}</span>
                </div>
              `
    })}
          </div>
        ` : ''}

        ${this.searchResults.length > 0 ? html`
          <div class="search-dropdown-section">
            <div class="search-dropdown-section-label">Results</div>
            ${this.searchResults.map((result) => {
      const idx = itemIndex++
      return html`
                <div
                  class="search-dropdown-item search-dropdown-item--result ${idx === this.activeIndex ? 'search-dropdown-item--active' : ''}"
                  role="option"
                  aria-selected="${idx === this.activeIndex}"
                  @mousedown=${(e: Event) => { e.preventDefault(); this._handleResultSelect(result) }}
                >
                  ${result.imageUrl ? html`
                    <img class="search-dropdown-result-thumb" src="${result.imageUrl}" alt="" aria-hidden="true" />
                  ` : ''}
                  <div class="search-dropdown-result-body">
                    ${result.contentType ? html`
                      <span class="search-dropdown-result-type">${result.contentType}</span>
                    ` : ''}
                    <span class="search-dropdown-result-title">${result.title}</span>
                    ${result.description ? html`
                      <span class="search-dropdown-result-description">${result.description}</span>
                    ` : ''}
                  </div>
                </div>
              `
    })}
          </div>
        ` : ''}

      </div>
    `
  }

  private renderNavigation() {
    return html`
      <nav class="center-section">
        ${this.navItems.map((item) => {
      return LigonierHeader.renderSafeLinkOrText(item.label, item.href, 'nav-link')
    })}
      </nav>
    `
  }

  private renderRightSection() {
    // TODO: Update this to use proper button text + style
    return html`
      <div class="right-section">
        ${this.showSearch ? html`
          <div class="search-container ${this.searchOpen ? 'open' : ''}">
            <form id="${this.desktopSearchFormId}" @submit=${this.handleSearchSubmit} class="search-form">
              <input
                id="${this.desktopSearchInputId}"
                type="text"
                class="search-input"
                placeholder="Search"
                autocomplete="off"
                role="combobox"
                aria-expanded="${this.showDropdown}"
                aria-haspopup="listbox"
                ${ref(this.desktopSearchInputRef)}
                .value=${this.searchQuery}
                @input=${this.handleSearchInput}
                @keydown=${this.handleSearchKeydown}
                @focus=${this.handleSearchFocus}
                @blur=${this.handleSearchBlur}
              />
            </form>
            ${this.renderSearchDropdown()}
            <button
              type="button"
              class="search-icon"
              aria-label="${this.searchButtonAriaLabel}"
              aria-expanded="${String(this.searchOpen)}"
              aria-controls="${this.desktopSearchFormId}"
              @click=${this.handleSearchClick}
            >
              <img src="${this.searchIconSrc}" alt="" aria-hidden="true" />
            </button>
          </div>
        ` : nothing}
        <div class="account-dropdown-container">
          <button
            type="button"
            class="avatar-button"
            aria-label="${this.isAuthenticated ? 'Account menu' : 'Sign in'}"
            aria-expanded="${ifDefined(this.isAuthenticated ? String(this.accountDropdownOpen) : undefined)}"
            aria-controls="${ifDefined(this.isAuthenticated ? 'account-dropdown' : undefined)}"
            aria-busy="${this.authLoading}"
            @click=${this.handleAvatarClick}
          >
            <img
              class="avatar"
              src="${this.avatarSrc}"
              alt="${this.avatarAltText}"
              data-authenticated="${this.isAuthenticated}"
            />
          </button>
          ${this.renderDesktopAccountDropdown()}
        </div>
        <div class="donate-icon-container">
          <button
            type="button"
            class="donate-icon-button"
            aria-label="Give"
            @click=${this.handleDonateClick}
          >
            <img class="donate-icon" src="${this.donateIconSrc}" alt="" aria-hidden="true" />
          </button>
        </div>
        ${this.showPartnerButton
        ? LigonierHeader.renderActionButton('PARTNER', 'partner-button', this.handlePartnerClick)
        : nothing}
        ${this.showDonateButton
        ? LigonierHeader.renderActionButton('DONATE', 'donate-button', this.handleDonateClick)
        : nothing}
      </div>
    `
  }

  private renderMobileSearch() {
    return html`
      <div class="mobile-search-container">
        <form @submit=${this.handleSearchSubmit} class="mobile-search-form">
          <input
            type="text"
            class="mobile-search-input"
            placeholder="Search"
            autocomplete="off"
            role="combobox"
            aria-expanded="${this.showDropdown}"
            aria-haspopup="listbox"
            .value=${this.searchQuery}
            @input=${this.handleSearchInput}
            @keydown=${this.handleSearchKeydown}
            @focus=${this.handleSearchFocus}
            @blur=${this.handleSearchBlur}
          />
          <img
            class="mobile-search-icon"
            src="${this.searchIconSrc}"
            alt=""
            aria-hidden="true"
          />
        </form>
        ${this.renderSearchDropdown()}
      </div>
    `
  }

  private renderDesktopAccountDropdown() {
    if (!this.accountDropdownOpen || !this.isAuthenticated) return nothing

    return html`
      <div id="account-dropdown" class="account-dropdown">
        <a href="${this.accountUrl}" class="account-dropdown-item">My Account</a>
        <a href="${this.libraryUrl}" class="account-dropdown-item">My Library</a>
        <button
          type="button"
          class="account-dropdown-item account-dropdown-signout"
          @click=${this.handleSignOut}
        >Sign out</button>
      </div>
    `
  }

  private renderMobileAccountSection() {
    const isExpanded = this.isSectionExpanded('My Account')
    const { buttonId: accountButtonId, contentId: accountContentId } = LigonierHeader.getAccordionIds(
      'My Account',
      'account',
    )

    if (!this.isAuthenticated) {
      // Signed-out users still get a "My Account" section exposing both auth
      // options (Create Account + Sign In), matching the reference menu — not a
      // bare "Sign in" button (DEV-1155). Sign In routes through handleAvatarClick
      // so SPA hosts can still intercept the cancelable auth-login-click event.
      const redirectUri = typeof window !== 'undefined' ? window.location.href : '/'
      const signedOutContent = html`
        <div class="menu-auth-buttons">
          <a
            href="${this.registerUrl(redirectUri)}"
            class="menu-auth-button menu-auth-button--primary"
            >Create Account</a
          >
          <button
            type="button"
            class="menu-auth-button menu-auth-button--secondary"
            @click=${this.handleAvatarClick}
          >
            Sign In
          </button>
        </div>
      `

      return this.renderAccordionSection(
        'My Account',
        accountButtonId,
        accountContentId,
        isExpanded,
        signedOutContent,
      )
    }

    const mobileAccountChildren = this.getMobileAccountChildren()

    const accountContent = html`
      ${mobileAccountChildren.map((child) => html`
        <div class="menu-item-wrapper">
          ${LigonierHeader.renderSafeLinkOrText(child.label, child.href, 'menu-item')}
        </div>
      `)}
      <div class="menu-item-wrapper">
        <button type="button" class="menu-item menu-item-signout" @click=${this.handleSignOut}>Sign out</button>
      </div>
    `

    return this.renderAccordionSection(
      'My Account',
      accountButtonId,
      accountContentId,
      isExpanded,
      accountContent,
    )
  }

  private renderMenuEmailSignup() {
    return html`
      <div class="menu-email-signup">
        <div class="menu-email-signup-header">
          <svg
            class="menu-email-signup-icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 0a1 1 0 0 1 1 1v6.589l3.626 2.63.067.06a.963.963 0 0 1 .132.156l.073.125-.02-.039c.041.076.072.155.092.235l.023.126L24 11v12l-.002.067-.011.098-.025.111-.025.074-.059.129-.04.066-.071.097-.06.065-.083.075-.122.083-.096.05-.096.036-.134.034-.058.008L23 24H1a1 1 0 0 1-1-1V10.99l.007-.105.023-.128.037-.117.036-.081.033-.062.044-.069-.058.093.04-.068.082-.108.081-.083a.962.962 0 0 1 .102-.082L4 7.59V1a1 1 0 0 1 1-1h14zM2 12.684V22h17.08L4.683 14.149a.998.998 0 0 1-.313-.172L2 12.684zm20-.001l-2.37 1.294a.998.998 0 0 1-.313.172l-5.23 2.85L22 21.315v-8.632zM18 2H6v10.588l6 3.273 6-3.273V2zm-3 7a1 1 0 1 1 0 2H9a1 1 0 1 1 0-2h6zm0-4a1 1 0 1 1 0 2H9a1 1 0 0 1 0-2h6z"
            />
          </svg>
          <h3 class="menu-email-signup-title">Stay in Touch</h3>
        </div>
        <form class="menu-email-signup-form" @submit=${this.handleEmailSignup}>
          <input
            type="email"
            name="email"
            class="menu-email-signup-input"
            placeholder="Enter your email address."
            aria-label="Email address"
            required
          />
          <button type="submit" class="menu-email-signup-button">Submit</button>
        </form>
      </div>
    `
  }

  private renderMenu() {
    const shouldHideMenuWhenClosed = !this.menuOpen && !LigonierHeader.supportsInert

    return html`
      <div class="menu-overlay ${this.menuOpen ? 'open' : ''}" @click=${this.handleMenuOverlayClick}>
        <div
          id="${this.menuContainerId}"
          class="menu-container"
          ?hidden=${shouldHideMenuWhenClosed}
          aria-hidden=${this.menuOpen ? 'false' : 'true'}
          ?inert=${!this.menuOpen}
        >
          <div class="menu-desktop-content">
            ${this.shouldShowMenuLoadingState || this.shouldShowMenuErrorState || this.shouldShowMenuEmptyState
        ? this.renderMenuStatusState()
        : this.desktopNavItems.map(item => {
          return html`
              <div class="menu-desktop-section">
                ${LigonierHeader.renderSafeLinkOrText(item.label, item.href, 'menu-desktop-title', 'div')}

                <div class="menu-desktop-items">
                  ${item.children.map(child => {
            if (child.divider) {
              return LigonierHeader.renderDivider('menu-desktop-divider')
            }

            const childClassName = LigonierHeader.getEventLinkClass('menu-desktop-link', child.meta)

            return html`
                      <div class="menu-desktop-item">
                        ${child.meta ? html`<div class="menu-desktop-meta">${child.meta}</div>` : ''}
                        ${LigonierHeader.renderSafeLinkOrText(child.label, child.href, childClassName)}
                      </div>
                    `
          })}

                  ${LigonierHeader.renderPromotionBlock(
            item.promotionLabel,
            item.promotionHref,
            'menu-desktop-divider',
            'menu-desktop-link featured',
            item.promotionTitle,
            item.promotionSubtitle,
            undefined,
            'menu-desktop-promo-title',
            'menu-desktop-promo-subtitle',
          )}
                </div>
              </div>
            `})}
          </div>

          <div class="menu-mobile-content">
            ${this.renderMobileAccountSection()}

            ${this.shouldShowMenuLoadingState || this.shouldShowMenuErrorState || this.shouldShowMenuEmptyState
        ? this.renderMenuStatusState()
        : nothing}

            ${this.shouldShowMenuLoadingState || this.shouldShowMenuErrorState || this.shouldShowMenuEmptyState
        ? nothing
        : this.navItems.map((item, index) => {
          if (this.hasChildren(item)) {
            const isExpanded = this.isSectionExpanded(item.label)
            const mobileOverviewLabel = item.mobileLabel || 'Overview'
            const { buttonId: sectionButtonId, contentId: sectionContentId } = LigonierHeader.getAccordionIds(
              item.label,
              index,
            )
            const safeItemHref = LigonierHeader.sanitizeHref(item.href)

            const sectionContent = html`
                  ${safeItemHref
                ? html`
                        <div class="menu-item-wrapper">
                          ${LigonierHeader.renderSafeLinkOrText(mobileOverviewLabel, safeItemHref, 'menu-item')}
                        </div>
                      `
                : nothing}

                  ${item.children.map((child) => {
                  if (child.divider) {
                    return LigonierHeader.renderDivider('menu-mobile-divider')
                  }

                  const childClassName = LigonierHeader.getEventLinkClass('menu-item', child.meta)
                  return html`
                      <div class="menu-item-wrapper">
                        ${child.meta ? html`<div class="menu-item date">${child.meta}</div>` : ''}
                        ${LigonierHeader.renderSafeLinkOrText(child.label, child.href, childClassName)}
                      </div>
                    `
                })}

                  ${LigonierHeader.renderPromotionBlock(
                  item.promotionLabel,
                  item.promotionHref,
                  'menu-mobile-divider',
                  'menu-item featured',
                  item.promotionTitle,
                  item.promotionSubtitle,
                  'menu-item-wrapper promotion',
                )}
                `

            return this.renderAccordionSection(
              item.label,
              sectionButtonId,
              sectionContentId,
              isExpanded,
              sectionContent,
            )
          } else {
            const mobileSectionLabel = item.mobileLabel || item.label

            return html`
                  <div class="menu-section">
                    ${LigonierHeader.renderSafeLinkOrText(mobileSectionLabel, item.href, 'section-title')}
                  </div>
                `
          }
        })}
          </div>

          ${this.renderMenuEmailSignup()}
        </div>
      </div>
    `
  }

  // Main render method
  render() {
    return html`
      <header
        data-authenticated="${this.isAuthenticated}"
        aria-busy="${this.authLoading}"
      >
        <div class="left-section">
          <button
            type="button"
            class="menu-button"
            @click=${this.handleMenuClick}
            aria-expanded="${String(this.menuOpen)}"
            aria-controls="${this.menuContainerId}"
          >
            ${this.menuButtonLabel}
          </button>
          <a href="${this.logoHref}" class="logo-link" @click=${this.handleLogoClick}>
            <img class="logo" src="${this.logoSrc}" alt="Ligonier Ministries logo" />
          </a>
        </div>

        ${this.renderNavigation()}
        ${this.renderRightSection()}
      </header>

      ${this.showSearch ? this.renderMobileSearch() : nothing}

      <!-- In-flow spacer that reserves the fixed header's footprint (header, plus
           the mobile search bar below the desktop breakpoint) so consuming apps
           don't manage top offset themselves. Reserves nothing on the clear
           theme, where content intentionally sits under the hero. -->
      <div class="header-spacer" aria-hidden="true"></div>

      ${this.renderMenu()}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ligonier-header': LigonierHeader
  }
}
